import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Button } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { Input } from 'ui/input/Input';
import { ActionButton } from 'ui/actionButton/ActionButton';
import { Direction, updateOrganizationTreeOrder } from 'app/definitions/utils/updateOrganizationTreeOrder';

import { OrganizationDetailsProps, UpdateOrganizationFormBody } from './OrganizationDetails.types';
import { OrganizationActions } from './organizationActions/OrganizationActions';
import { getParentElement } from './OrganizationDetails.utils';
import * as Styled from './OrganizationDetails.styles';

const SORT_BUTTON_SIZE = 48;

export const OrganizationDetails = ({
  onSubmit,
  isLoading,
  selectedOrganization,
  mainOrganization,
  setOrganizations,
  organizations,
}: OrganizationDetailsProps) => {
  const { formatMessage } = useLocale();

  const parentElement = getParentElement(mainOrganization, selectedOrganization);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty, isValid },
    watch,
  } = useFormContext<UpdateOrganizationFormBody>();

  const updateOrder = useCallback(
    (direction: Direction) => {
      setOrganizations((prevOrganizations) => {
        if (!prevOrganizations) return prevOrganizations;

        return prevOrganizations.map((org) => {
          if (org.id === mainOrganization.id) {
            return updateOrganizationTreeOrder(org, selectedOrganization, direction);
          }
          return org;
        });
      });
    },
    [selectedOrganization, mainOrganization],
  );

  const { order } = watch();

  return (
    <Styled.Form onSubmit={handleSubmit(onSubmit)}>
      <div>
        <OrganizationActions mainOrganization={mainOrganization} selectedOrganization={selectedOrganization} />

        <Controller
          name="kod"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label={formatMessage({ id: AppMessages['definitions.organization.add.code.label'] })}
              placeholder={formatMessage({ id: AppMessages['definitions.organization.add.code.placeholder'] })}
              errorMessage={error?.message}
            />
          )}
        />

        <Controller
          name="nazwa"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label={formatMessage({ id: AppMessages['definitions.organization.add.name.label'] })}
              placeholder={formatMessage({ id: AppMessages['definitions.organization.add.name.placeholder'] })}
              errorMessage={error?.message}
            />
          )}
        />

        <Styled.Label>
          {formatMessage({ id: AppMessages['definitions.organization.details.order.title'] })}
        </Styled.Label>
        <Styled.DescriptionWrapper>
          <Styled.Description>
            {formatMessage({ id: AppMessages['definitions.organization.details.order.description'] })}
          </Styled.Description>
        </Styled.DescriptionWrapper>
        <Styled.SortButtonsWrapper>
          <ActionButton
            type="arrowUp"
            onClick={() => updateOrder('up')}
            style={{ width: SORT_BUTTON_SIZE, height: SORT_BUTTON_SIZE }}
            disabled={!parentElement || order === 0}
          />

          <ActionButton
            type="arrowDown"
            onClick={() => updateOrder('down')}
            style={{ width: SORT_BUTTON_SIZE, height: SORT_BUTTON_SIZE }}
            disabled={!parentElement || (parentElement?.children && parentElement.children.length - 1 === order)}
          />
        </Styled.SortButtonsWrapper>
      </div>
      {(isDirty || order !== selectedOrganization.order) && (
        <Styled.ButtonsWrapper>
          <Button
            size="small"
            onClick={() => {
              reset(selectedOrganization);
              setOrganizations(organizations);
            }}
            color="primary"
          >
            {formatMessage({ id: AppMessages['event.details.form.button.cancel'] })}
          </Button>
          <Button
            type="submit"
            disabled={!isValid}
            isLoading={isLoading}
            size="small"
            variant="contained"
            color="primary"
          >
            {formatMessage({ id: AppMessages['event.details.form.button.save'] })}
          </Button>
        </Styled.ButtonsWrapper>
      )}
    </Styled.Form>
  );
};
